<template>
  <div class="container-fluid">
    <!-- One action per day: the receiving establishment turns the day into an
         internal invoice, prefilled with what is left to invoice. -->
    <div class="row mb-4">
      <div class="col-12">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>{{ $t("COMMON.DATE") }}</th>
              <th class="text-right">{{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ORDERED") }}</th>
              <th class="text-right">
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.INVOICED") }}
              </th>
              <th class="text-right">{{ $t("COMMON.ACTIONS") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="day in weekDays" :key="day">
              <td>{{ dayLabel(day) }}</td>
              <td class="text-right">{{ dayState(day).ordered_quantity || 0 }}</td>
              <td class="text-right">{{ dayState(day).invoiced_quantity || 0 }}</td>
              <td class="text-right">
                <!-- One invoice settles its whole day: what was not received
                     with it is not waited for nor rebilled another day. -->
                <span
                  v-if="dayState(day).lines && dayState(day).non_received"
                  class="text-warning"
                >
                  <i class="fas fa-ban mr-1"></i>
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DAY_NOT_RECEIVED") }}
                </span>
                <span
                  v-else-if="
                    dayState(day).lines &&
                    (dayState(day).has_invoice || dayState(day).fully_invoiced)
                  "
                  class="text-success"
                >
                  <i class="fas fa-check mr-1"></i>
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DAY_FULLY_INVOICED") }}
                </span>
                <base-button
                  v-else-if="canGenerate && dayState(day).lines"
                  type="primary"
                  size="sm"
                  @click="openGenerateModal(day)"
                >
                  <i class="fas fa-file-invoice-dollar mr-1"></i>
                  {{
                    $t("ESTABLISHMENT_INTERNAL_ORDERS.GENERATE_INVOICE_FOR_DAY", {
                      day: dayLabel(day),
                    })
                  }}
                </base-button>
                <span v-else-if="!orderIsInvoiceable" class="text-muted">
                  {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRM_BEFORE_INVOICING") }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ordered / invoiced per product and per day -->
    <div class="row">
      <div class="col-12 table-responsive">
        <table class="table table-sm table-bordered">
          <thead class="thead-light">
            <tr>
              <th>{{ $t("COMMON.PRODUCT") }}</th>
              <th v-for="day in weekDays" :key="day" class="text-right">
                {{ dayLabel(day) }}
              </th>
              <th class="text-right">
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ORDERED") }}
              </th>
              <th class="text-right">
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.INVOICED") }}
              </th>
              <th>{{ $t("COMMON.STATUS") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in productRows" :key="row.productId">
              <td>
                {{ row.name }}
                <div class="text-muted small">{{ row.code }}</div>
              </td>
              <td v-for="day in weekDays" :key="day" class="text-right">
                <span v-if="row.cells[day]" :class="cellClass(row.cells[day])">
                  {{ row.cells[day].ordered }} / {{ row.cells[day].invoiced }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="text-right">{{ row.ordered }}</td>
              <td class="text-right">{{ row.invoiced }}</td>
              <td>
                <badge :type="stateBadgeType(row)">
                  {{ $t(`ESTABLISHMENT_INTERNAL_ORDERS.${stateLabelKey(row)}`) }}
                </badge>
              </td>
            </tr>
            <tr v-if="!productRows.length">
              <td :colspan="weekDays.length + 4" class="text-center text-muted">
                {{ $t("COMMON.NO_DATA") }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <establishment-internal-order-generate-invoice-modal
      v-if="order.id"
      :order="order"
      :day="selectedDay"
      :showModal="showGenerateModal"
      @onCloseModal="showGenerateModal = false"
      @generated="onGenerated"
    />
  </div>
</template>

<script>
import moment from "moment";
import { establishmentInternalOrderInvoiceableStatuses } from "@/constants/establishmentInternalOrders";
import EstablishmentInternalOrderGenerateInvoiceModal from "./EstablishmentInternalOrderGenerateInvoiceModal.vue";

export default {
  name: "establishment-internal-order-view-tracking",
  components: { EstablishmentInternalOrderGenerateInvoiceModal },
  props: {
    order: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showGenerateModal: false,
      selectedDay: null,
    };
  },
  computed: {
    state() {
      return this.order.invoicing_items_state || {};
    },
    weekDays() {
      if (this.order.week_days && this.order.week_days.length) {
        return this.order.week_days;
      }
      if (!this.order.week_starts_at) return [];
      const start = moment(this.order.week_starts_at);
      const end = this.order.week_ends_at
        ? moment(this.order.week_ends_at)
        : start.clone().add(4, "days");
      const days = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, "day")) {
        days.push(cursor.format("YYYY-MM-DD"));
        cursor.add(1, "day");
      }
      return days;
    },
    /**
     * A week can only be invoiced once the production side has confirmed it.
     */
    orderIsInvoiceable() {
      return establishmentInternalOrderInvoiceableStatuses.includes(this.order.status);
    },
    canGenerate() {
      return (
        this.orderIsInvoiceable &&
        this.$currentUserCan(this.$permissions.PERM_CREATE_ESTABLISHMENT_INTERNAL_INVOICES)
      );
    },
    /**
     * One row per product, holding an ordered/invoiced pair per day.
     */
    productRows() {
      const ordered = this.state.orderedItems || {};
      const invoiced = this.state.invoicedItems || {};
      const itemsById = {};
      (this.order.items || []).forEach((item) => {
        itemsById[String(item.id)] = item;
      });

      const rows = {};
      Object.keys(ordered).forEach((key) => {
        const line = ordered[key];
        const item = itemsById[key];
        const productId = line.storable_id || item?.storable?.id;
        if (!productId) return;

        if (!rows[productId]) {
          rows[productId] = {
            productId,
            name: item?.storable?.name || `#${productId}`,
            code: item?.storable?.code || "",
            ordered: 0,
            invoiced: 0,
            cells: {},
          };
        }

        const orderedQuantity = parseFloat(line.quantity || 0);
        const invoicedQuantity = parseFloat(invoiced[key]?.quantity || 0);

        rows[productId].ordered += orderedQuantity;
        rows[productId].invoiced += invoicedQuantity;
        rows[productId].cells[line.delivery_date] = {
          ordered: orderedQuantity,
          invoiced: invoicedQuantity,
        };
      });

      return Object.values(rows).sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    dayLabel(day) {
      return moment(day).format("ddd DD/MM");
    },
    dayState(day) {
      return (this.state.byDay || {})[day] || {};
    },
    cellClass(cell) {
      if (cell.invoiced > cell.ordered) return "text-info font-weight-bold";
      if (cell.invoiced === cell.ordered) return "";
      if (cell.invoiced === 0) return "text-danger";
      return "text-warning font-weight-bold";
    },
    stateLabelKey(row) {
      if (row.invoiced > row.ordered) return "LINE_STATE_OVER";
      if (row.invoiced === row.ordered) return "LINE_STATE_CONFORM";
      if (row.invoiced === 0) return "LINE_STATE_NOT_RECEIVED";
      return "LINE_STATE_PARTIAL";
    },
    stateBadgeType(row) {
      switch (this.stateLabelKey(row)) {
        case "LINE_STATE_OVER":
          return "info";
        case "LINE_STATE_CONFORM":
          return "success";
        case "LINE_STATE_NOT_RECEIVED":
          return "danger";
        default:
          return "warning";
      }
    },
    openGenerateModal(day) {
      this.selectedDay = day;
      this.showGenerateModal = true;
    },
    onGenerated() {
      this.$emit("onInvoiceGenerated");
    },
  },
};
</script>
